import React, { useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { FetchStatus, EventType, ErrorCode } from "../commons/Constants";
import { useLanderConfig } from "../commons/ConfigContext";
import { isRollerAdsKeywordClickGeo } from "../commons/HelperFunctions";
import { fetchRollerAds } from "./rollerAdsFeed";
import { sendRollerAdsEvent } from "./rollerAdsEvent";
import { trackImpression, trackClick } from "../commons/signals";
import Logger from "../commons/Logger";
import { RollerAdsKeywordLinks } from "./RollerAdsKeywordLinks";

function isValidRedirectUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

function getKeywordClickState(
  landerConfig: {
    rollerAds?: { keywords?: string[] };
  },
  mergedKeywords: string[],
  isKeywordClickGeo: boolean
): { isKeywordClickFlow: boolean; keywords: string[] } {
  if (!isKeywordClickGeo) {
    return { isKeywordClickFlow: false, keywords: [] };
  }

  const rollerAdsKeywords = landerConfig.rollerAds?.keywords;
  const keywords =
    rollerAdsKeywords && rollerAdsKeywords.length > 0
      ? rollerAdsKeywords
      : mergedKeywords;
  return { isKeywordClickFlow: keywords.length > 0, keywords };
}

export const RollerAdsContent: React.FC = () => {
  const { landerConfig, mergedConfig } = useLanderConfig();
  const [fetchState, setFetchState] = useState<number>(FetchStatus.PENDING);

  const { isKeywordClickFlow, keywords: keywordClickKeywords } =
    getKeywordClickState(
      landerConfig,
      mergedConfig?.keywords ?? [],
      isRollerAdsKeywordClickGeo()
    );

  // Fire VISIT event on mount
  useEffect(() => {
    sendRollerAdsEvent(EventType.VISIT, landerConfig);
    trackImpression({ section: "page", widget: "lander_page" });
  }, [landerConfig]);

  // 0-click auto-redirect (skipped for keyword click flow)
  useEffect(() => {
    if (isKeywordClickFlow) return;

    let ignore = false;

    fetchRollerAds({
      rootDomain: landerConfig.domain.rootDomain,
      customerId: landerConfig.customerId,
      keywords: mergedConfig?.keywords,
    }).then((result) => {
      if (ignore) return;

      if (result.status === 200 && result.ads.length > 0) {
        const firstAd = result.ads[0];

        if (!isValidRedirectUrl(firstAd.url)) {
          setFetchState(FetchStatus.FAILURE);
          sendRollerAdsEvent(EventType.ERROR, landerConfig, {
            errorCode: ErrorCode.ERROR_ROLLERADS_INVALID_URL,
          });
          Logger.error("RollerAds invalid redirect URL:", firstAd.url);
          return;
        }

        setFetchState(FetchStatus.SUCCESS);

        trackClick({ section: "ads", widget: "ad_redirect" });
        sendRollerAdsEvent(EventType.AD_REDIRECT, landerConfig, {
          bid: firstAd.bid,
          campaignId: firstAd.campaign_id,
          adUrl: firstAd.url,
        });

        window.location.href = firstAd.url;
      } else if (result.status === 0) {
        setFetchState(FetchStatus.FAILURE);
        sendRollerAdsEvent(EventType.ERROR, landerConfig, {
          errorCode: ErrorCode.ERROR_ROLLERADS_BLOCKED,
        });
        Logger.error("RollerAds blocked or missing client IP:", result.error);
      } else if (result.status === 204 || result.ads.length === 0) {
        setFetchState(FetchStatus.FAILURE);
        sendRollerAdsEvent(EventType.ERROR, landerConfig, {
          errorCode: ErrorCode.ERROR_ROLLERADS_NO_ADS,
        });
      } else {
        setFetchState(FetchStatus.FAILURE);
        sendRollerAdsEvent(EventType.ERROR, landerConfig, {
          errorCode: ErrorCode.ERROR_ROLLERADS_FETCH,
        });
        Logger.error("RollerAds error:", result.error);
      }
    });

    return () => {
      ignore = true;
    };
  }, [landerConfig, mergedConfig?.keywords, isKeywordClickFlow]);

  const handleKeywordClick = useCallback(
    async (keyword: string) => {
      setFetchState(FetchStatus.PENDING);

      sendRollerAdsEvent(EventType.KEYWORD_CLICK, landerConfig, { keyword });

      const result = await fetchRollerAds({
        rootDomain: landerConfig.domain.rootDomain,
        customerId: landerConfig.customerId,
        keywords: [keyword],
        keywordClick: true,
      });

      if (result.status === 200 && result.ads.length > 0) {
        const firstAd = result.ads[0];

        if (!isValidRedirectUrl(firstAd.url)) {
          setFetchState(FetchStatus.FAILURE);
          sendRollerAdsEvent(EventType.ERROR, landerConfig, {
            errorCode: ErrorCode.ERROR_ROLLERADS_INVALID_URL,
          });
          return;
        }

        trackClick({ section: "ads", widget: "ad_redirect" });
        sendRollerAdsEvent(EventType.AD_REDIRECT, landerConfig, {
          bid: firstAd.bid,
          campaignId: firstAd.campaign_id,
          adUrl: firstAd.url,
          keyword,
        });

        window.location.href = firstAd.url;
      } else {
        setFetchState(FetchStatus.FAILURE);
        sendRollerAdsEvent(EventType.ERROR, landerConfig, {
          errorCode:
            result.status === 0
              ? ErrorCode.ERROR_ROLLERADS_BLOCKED
              : ErrorCode.ERROR_ROLLERADS_NO_ADS,
        });
      }
    },
    [landerConfig]
  );

  // 1-click keyword links mode
  if (isKeywordClickFlow) {
    return (
      <RollerAdsKeywordLinks
        keywords={ keywordClickKeywords }
        fetchState={ fetchState }
        onKeywordClick={ handleKeywordClick }
      />
    );
  }

  // 0-click mode rendering
  if (fetchState === FetchStatus.PENDING) {
    return null;
  }

  if (fetchState === FetchStatus.FAILURE) {
    return (
      <div>
        <FormattedMessage id="contentBlockedText" />
      </div>
    );
  }

  return (
    <div>
      <p>
        <FormattedMessage
          id="redirectingText"
          defaultMessage="Redirecting..."
        />
      </p>
    </div>
  );
};
